import {
  KendamaBrand,
  KendamaModel,
  KendamaCategory,
  KendamaType,
  LocalizedText,
} from './kendamaCatalog';
import { KendamaSubmission, KendamaSubmissionStatus } from './types';
import { isLocalBackend, phpApiUrl, phpAuthHeaders } from './apiEnv';

const buildUrl = (path: string, action: string) =>
  isLocalBackend() ? path : phpApiUrl(action);

// 原生 App 的跨域请求不能携带 Cookie：服务端 ACAO 为 *，带凭证会被浏览器拦截（Load failed）。
// 跨域一律靠 X-Kendama-Auth 头鉴权，仅本地开发后端使用 Cookie。
const buildRequestCredentials = (): RequestCredentials =>
  isLocalBackend() ? 'include' : 'omit';

const isJsonResponse = (response: Response) => {
  const contentType = response.headers.get('content-type') || '';
  return contentType.toLowerCase().includes('application/json');
};

export const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('图片读取失败，请重试。'));
    };
    reader.onerror = () => reject(new Error('图片读取失败，请重试。'));
    reader.readAsDataURL(file);
  });

const ensureLocalized = (value: unknown, fallback: string): LocalizedText => {
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return {
      zh: typeof obj.zh === 'string' ? obj.zh : fallback,
      en: typeof obj.en === 'string' ? obj.en : fallback,
      ja: typeof obj.ja === 'string' ? obj.ja : fallback,
    };
  }
  return { zh: fallback, en: fallback, ja: fallback };
};

const normalizeCategory = (value: unknown): KendamaCategory => {
  const categories: KendamaCategory[] = ['entry', 'competition', 'signature', 'limited', 'standard', 'premium'];
  return categories.includes(value as KendamaCategory) ? (value as KendamaCategory) : 'standard';
};

const normalizeType = (value: unknown): KendamaType => {
  const types: KendamaType[] = ['ken', 'tama', 'bundle', 'novelty'];
  return types.includes(value as KendamaType) ? (value as KendamaType) : 'ken';
};

const normalizeModel = (model: Partial<KendamaModel>, index: number): KendamaModel | null => {
  const name = String(model?.name || '').trim();
  if (!name) return null;
  const fallbackId = `kendama-model-${Date.now()}-${index + 1}`;
  const type = normalizeType(model?.type);
  const legacyImage = typeof model?.image === 'string' && model.image.trim() ? model.image.trim() : '';
  const images = (Array.isArray(model?.images) ? model.images : [])
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean);
  const normalizedImages = images.length ? images : legacyImage ? [legacyImage] : [];
  return {
    id: String(model?.id || fallbackId).trim() || fallbackId,
    name,
    nameZh: String(model?.nameZh || name).trim() || name,
    nameJa: String(model?.nameJa || name).trim() || name,
    type,
    shape: String(model?.shape || (type === 'ken' ? 'Custom' : 'Round')).trim() || (type === 'ken' ? 'Custom' : 'Round'),
    category: normalizeCategory(model?.category),
    material: String(model?.material || 'Beech').trim() || 'Beech',
    paint: typeof model?.paint === 'string' && model.paint.trim() ? model.paint.trim() : undefined,
    // 第一张图作为封面，与 images 保持一致。
    image: normalizedImages[0],
    images: normalizedImages,
    tags: Array.isArray(model?.tags) ? model.tags.map((tag) => String(tag)) : [],
    description: ensureLocalized(model?.description, ''),
  };
};

const normalizeBrand = (brand: Partial<KendamaBrand>, index: number): KendamaBrand | null => {
  const name = String(brand?.name || '').trim();
  if (!name) return null;
  const fallbackId = `kendama-brand-${Date.now()}-${index + 1}`;
  const models = Array.isArray(brand?.models)
    ? brand.models
        .map((model, modelIndex) => normalizeModel(model as Partial<KendamaModel>, modelIndex))
        .filter((model): model is KendamaModel => !!model)
    : [];
  return {
    id: String(brand?.id || fallbackId).trim() || fallbackId,
    name,
    nameEn: typeof brand?.nameEn === 'string' && brand.nameEn.trim() ? brand.nameEn.trim() : undefined,
    nameZh: String(brand?.nameZh || name).trim() || name,
    nameJa: String(brand?.nameJa || name).trim() || name,
    country: String(brand?.country || '').trim(),
    countryZh: String(brand?.countryZh || brand?.country || '').trim(),
    countryJa: String(brand?.countryJa || brand?.country || '').trim(),
    accent: typeof brand?.accent === 'string' && brand.accent.trim() ? brand.accent.trim() : '#22d3ee',
    description: ensureLocalized(brand?.description, ''),
    models,
  };
};

export const normalizeKendamaCatalog = (raw: unknown): KendamaBrand[] => {
  if (!Array.isArray(raw)) return [];
  const seen = new Map<string, KendamaBrand>();
  raw.forEach((brand, index) => {
    const normalized = normalizeBrand(brand as Partial<KendamaBrand>, index);
    if (normalized && !seen.has(normalized.id)) {
      seen.set(normalized.id, normalized);
    }
  });
  return Array.from(seen.values());
};

export const mergeKendamaBrands = (base: KendamaBrand[], extra: KendamaBrand[]): KendamaBrand[] => {
  const map = new Map<string, KendamaBrand>();
  base.forEach((brand) => map.set(brand.id, { ...brand, models: [...brand.models] }));
  extra.forEach((brand) => {
    const existing = map.get(brand.id);
    if (existing) {
      const existingIds = new Set(existing.models.map((model) => model.id));
      existing.models.push(...brand.models.filter((model) => !existingIds.has(model.id)));
    } else {
      map.set(brand.id, { ...brand, models: [...brand.models] });
    }
  });
  return Array.from(map.values());
};

export const fetchKendamaCatalog = async (signal?: AbortSignal): Promise<KendamaBrand[]> => {
  try {
    const response = await fetch(buildUrl('/api/kendama-catalog', 'get_kendama_catalog'), {
      credentials: buildRequestCredentials(),
      cache: 'no-store',
      headers: phpAuthHeaders(),
      signal,
    });
    if (response.ok && isJsonResponse(response)) {
      const data = await response.json();
      return normalizeKendamaCatalog(data.catalog);
    }
  } catch (error) {
    if ((error as Error)?.name === 'AbortError' || signal?.aborted) throw error;
    console.error('Fetch kendama catalog error:', error);
  }
  return [];
};

export const submitKendamaSubmission = async (payload: {
  brandName: string;
  modelName: string;
  image?: string;
}): Promise<{ message?: string }> => {
  const response = await fetch(buildUrl('/api/user/kendama-submissions', 'user_submit_kendama_submission'), {
    method: 'POST',
    credentials: buildRequestCredentials(),
    headers: phpAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      brandName: payload.brandName.trim(),
      modelName: payload.modelName.trim(),
      image: typeof payload.image === 'string' ? payload.image : '',
    }),
  });

  if (!response.ok) {
    const data = isJsonResponse(response) ? await response.json().catch(() => ({})) : {};
    throw new Error(String(data?.error || '提交反馈失败，请稍后重试。'));
  }

  if (!isJsonResponse(response)) return { message: '反馈已提交。' };
  const data = await response.json();
  return { message: typeof data?.message === 'string' ? data.message : '反馈已提交。' };
};

export const fetchAdminKendamaData = async (signal?: AbortSignal): Promise<{
  submissions: KendamaSubmission[];
  catalog: KendamaBrand[];
}> => {
  const response = await fetch(buildUrl('/api/admin/kendama-submissions', 'admin_get_kendama_submissions'), {
    credentials: buildRequestCredentials(),
    cache: 'no-store',
    headers: phpAuthHeaders(),
    signal,
  });
  if (!response.ok) {
    const data = isJsonResponse(response) ? await response.json().catch(() => ({})) : {};
    throw new Error(String(data?.error || '加载管理员数据失败。'));
  }
  const data = await response.json();
  return {
    submissions: Array.isArray(data.submissions) ? data.submissions : [],
    catalog: normalizeKendamaCatalog(data.catalog),
  };
};

export const updateKendamaSubmissionStatus = async (
  id: string,
  status: KendamaSubmissionStatus,
): Promise<void> => {
  const response = await fetch(buildUrl('/api/admin/kendama-submissions', 'admin_update_kendama_submission'), {
    method: 'POST',
    credentials: buildRequestCredentials(),
    headers: phpAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ id, status }),
  });
  if (!response.ok) {
    const data = isJsonResponse(response) ? await response.json().catch(() => ({})) : {};
    throw new Error(String(data?.error || '更新状态失败。'));
  }
};

export const saveKendamaCatalog = async (catalog: KendamaBrand[]): Promise<KendamaBrand[]> => {
  const response = await fetch(buildUrl('/api/admin/kendama-catalog', 'admin_save_kendama_catalog'), {
    method: 'POST',
    credentials: buildRequestCredentials(),
    headers: phpAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ catalog }),
  });
  if (!response.ok) {
    const data = isJsonResponse(response) ? await response.json().catch(() => ({})) : {};
    throw new Error(String(data?.error || '保存剑玉目录失败。'));
  }
  const data = await response.json();
  return normalizeKendamaCatalog(data.catalog);
};
